import { Injectable } from '@angular/core';
import { interval, merge, Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { ConnectionStatusService } from '../connection-status.service';
import { ImmoIPTourOrderChangesService } from '../immo-ip-placer-offline-changes/immo-ip-tour-order-changes/immo-ip-tour-order-changes.service';
import { ImmoIPPlacerTourOrderSubmitQueueService } from './immo-ip-placer-tour-order-submit-queue.service';
import { SnackService } from '../snack.service';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { ImmoIPTourOrderChangesObject } from '../immo-ip-placer-offline-changes/immo-ip-tour-order-changes/immo-ip-tour-order-changes-object';
import { ReorderTourItemsControllerRequestService } from 'src/app/pages/immo-ip/tour/tour-page/employee-tour-edit-modal/service/reorder-tour-items-controller-request.service';
import ErrorHelper from 'src/app/helpers/error-helper';

@Injectable({
  providedIn: 'root'
})
export class ImmoIPPlacerTourOrderSubmitService {
  private isSubmitting: boolean = false;
  private online: boolean = true;
  private readonly trySubmitInterval = 1000 * 10; // 10 seconds
  private readonly throttleInterval = 200; // 200 ms
  private submitTrigger = new Subject<any>();

  constructor(
    private connectionStatusService: ConnectionStatusService,
    private immoIPTourOrderChangesService: ImmoIPTourOrderChangesService,
    private immoIPPlacerTourOrderSubmitQueueService: ImmoIPPlacerTourOrderSubmitQueueService,
    private reorderTourItemsControllerRequestService: ReorderTourItemsControllerRequestService,
    private snackService: SnackService
  ) {
    this.connectionStatusService
      .getConnectionStatusSubject()
      .subscribe((online: boolean) => {
        const doSubmit = !this.online && online;
        this.online = online;
        if (doSubmit) {
          this.submitTrigger.next();
        }
      });
    merge(this.submitTrigger, interval(this.trySubmitInterval)).subscribe(
      () => {
        this.doSubmit();
      }
    );
  }

  private async doSubmit(): Promise<void> {
    if (this.isSubmitting || !this.online) {
      return;
    }
    this.isSubmitting = true;
    try {
      let toSubmit = await this.immoIPPlacerTourOrderSubmitQueueService.getToSubmit();
      while (CollectionHelper.isPopulated(toSubmit) && this.online) {
        // we don't want to wait 10 seconds if user has submitted tour item order change while the current queue is being processed
        for (const submitEntry of toSubmit) {
          const tourOrderChangesObject = await this.immoIPTourOrderChangesService.getObject(
            submitEntry
          );
          if (tourOrderChangesObject == null) {
            await this.immoIPPlacerTourOrderSubmitQueueService.removeToSubmit(
              submitEntry
            );
          } else {
            await this.doSubmitTourOrderChanges(tourOrderChangesObject);
          }
        }

        const reloadToSubmitPromise = new Promise<void>((resolve, reject) => {
          setTimeout(async () => {
            toSubmit = await this.immoIPPlacerTourOrderSubmitQueueService.getToSubmit();
            resolve();
          }, this.throttleInterval);
        });
        await reloadToSubmitPromise;
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doSubmitTourOrderChanges(
    tourOrderChangesObject: ImmoIPTourOrderChangesObject
  ) {
    try {
      await this.reorderTourItemsControllerRequestService.reorderTourItemPairs(
        tourOrderChangesObject.id,
        tourOrderChangesObject.tourItemIdSequenceNumberPairs
      );
      await this.immoIPTourOrderChangesService.removeIfUuidMatch(
        tourOrderChangesObject.id,
        tourOrderChangesObject.uuid
      );
    } catch (err) {
      if (!ErrorHelper.isConnectionLostError(err)) {
        this.snackService.showError(err);
        await this.immoIPTourOrderChangesService.removeObject(
          tourOrderChangesObject
        );
      }
    }
  }

  public async submitTourOrderChanges(
    tourOrderChangesObject: ImmoIPTourOrderChangesObject
  ): Promise<void> {
    if (tourOrderChangesObject.uuid == null) {
      tourOrderChangesObject.uuid = uuidv4();
    }
    const storeChangesPromise = this.immoIPTourOrderChangesService.storeObject(
      tourOrderChangesObject
    ); // should overwrite earlier stored order changes
    const storeSubmitQueuePromise = this.immoIPPlacerTourOrderSubmitQueueService.addToSubmit(
      tourOrderChangesObject.id
    );
    await Promise.all([storeChangesPromise, storeSubmitQueuePromise]);
    this.submitTrigger.next();
  }
}
